import React from 'react';
import {
  MEMBERSHIP_CUSTOM_FIELD_TYPES,
  MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS,
  MEMBERSHIP_LABEL_MAX_LENGTH,
  MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH,
  MEMBERSHIP_FORM_FIELD_DEFS,
  DEFAULT_MEMBERSHIP_TYPE_OPTIONS,
  DEFAULT_PLAYER_TYPE_OPTIONS,
} from '../constants/membership';
import { sanitizeMembershipCustomType, sanitizeMembershipCustomOptions } from '../utils/membership';
import { useSiteMembershipDefaults } from '../utils/siteDefaults';

function MembershipFormBuilder({ addCustomMembershipField, club, clubEditForm, dragMembershipFieldKey, getMembershipConfigFromForm, moveMembershipFieldByOffset, newMembershipFieldDetail, newMembershipFieldLabel, newMembershipFieldOptions, newMembershipFieldPersonal, newMembershipFieldRequired, newMembershipFieldStorage, newMembershipFieldType, removeMembershipField, reorderMembershipField, setClubEditForm, setDragMembershipFieldKey, setNewMembershipFieldDetail, setNewMembershipFieldLabel, setNewMembershipFieldOptions, setNewMembershipFieldPersonal, setNewMembershipFieldRequired, setNewMembershipFieldStorage, setNewMembershipFieldType, updateMembershipFieldConfig, users }) {
  const siteDefaults = useSiteMembershipDefaults();
  /* The built-in selects have no editable cell here, and the one line that
     stood in for one — "Managed by club option lists" — was both vague and,
     for Age Group, untrue: those four bands are fixed and editable nowhere.
     Show the values themselves, and say where each list actually comes from. */
  const resolveBuiltInSelectOptions = (field) => {
    if (field.optionsSource === 'membershipTypeOptions') {
      const opts = clubEditForm.membershipTypeOptions;
      return {
        options: (Array.isArray(opts) && opts.length) ? opts : DEFAULT_MEMBERSHIP_TYPE_OPTIONS,
        note: 'Club list — edit in Membership Types above',
      };
    }
    if (field.optionsSource === 'playerTypeOptions') {
      const opts = clubEditForm.playerTypeOptions;
      return {
        options: (Array.isArray(opts) && opts.length) ? opts : DEFAULT_PLAYER_TYPE_OPTIONS,
        note: 'Club list — edit in Player Types above',
      };
    }
    if (field.optionsSource === 'ageGroupOptions') {
      return {
        options: siteDefaults.ageGroupOptions,
        note: 'Site-wide list — a super-admin sets it for every club',
      };
    }
    const def = MEMBERSHIP_FORM_FIELD_DEFS.find(d => d.key === field.key);
    const options = (Array.isArray(field.options) && field.options.length)
      ? field.options
      : ((def && Array.isArray(def.options)) ? def.options : []);
    return { options, note: 'Fixed list — the same for every club' };
  };

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div id="membership-form-settings"></div>
      <label className="w3-tiny w3-text-grey"><i className="fa fa-wpforms w3-margin-right"></i>Public Membership Form</label>
      {(() => {
        const membershipCfg = getMembershipConfigFromForm(clubEditForm);
        return (
          <div className="w3-border w3-round" style={{ marginTop: '6px', padding: '10px', background: '#fafafa' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={!!membershipCfg.enabled}
                onChange={e => setClubEditForm(f => ({
                  ...f,
                  membershipFormConfig: { ...getMembershipConfigFromForm(f), enabled: !!e.target.checked },
                }))}
              />
              <span className="w3-small"><strong>Enable online membership applications</strong></span>
            </label>

            <div className="w3-border w3-round" style={{ padding: '10px', marginBottom: '10px', background: '#fff' }}>
              <div>
                <label className="w3-tiny w3-text-grey">Form Intro Text</label>
                <textarea
                  className="w3-input w3-border w3-round w3-small"
                  rows={3}
                  maxLength={MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH}
                  value={membershipCfg.introText || ''}
                  onChange={e => setClubEditForm(f => ({
                    ...f,
                    membershipFormConfig: { ...getMembershipConfigFromForm(f), introText: e.target.value },
                  }))}
                />
              </div>
              <div style={{ marginTop: '10px' }}>
                <label className="w3-tiny w3-text-grey">Secretary / Notification Emails (comma or newline separated)</label>
                <textarea
                  className="w3-input w3-border w3-round w3-small"
                  rows={2}
                  maxLength={MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH}
                  value={membershipCfg.secretaryEmails || ''}
                  onChange={e => setClubEditForm(f => ({
                    ...f,
                    membershipFormConfig: { ...getMembershipConfigFromForm(f), secretaryEmails: e.target.value },
                  }))}
                  placeholder={'secretary@club.org, membership@club.org\nmanager@club.org'}
                />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={membershipCfg.showClubEmailPublicly !== false}
                onChange={e => setClubEditForm(f => ({
                  ...f,
                  membershipFormConfig: {
                    ...getMembershipConfigFromForm(f),
                    showClubEmailPublicly: !!e.target.checked,
                  },
                }))}
              />
              <span className="w3-small">Show club email on public membership pages</span>
            </label>

            <div className="w3-small w3-text-grey" style={{ marginBottom: '6px' }}>
              Choose which fields are shown. Set storage to <strong>Email only</strong> for sensitive data that must not be saved in GreenSheet.
            </div>
            <div className="w3-small w3-text-grey" style={{ marginBottom: '6px' }}>
              Tick <strong>Personal</strong> for fields that only the member and club admins should see in player profiles.
            </div>
            <div className="w3-small w3-text-grey" style={{ marginBottom: '6px' }}>
              Club profile-required fields are locked: Full Name, Email, Bowling Start Year, Membership Type, Player Type.
            </div>
            <div className="w3-small w3-text-grey" style={{ marginBottom: '6px' }}>
              To remove an optional default field (for example Address), use the hide button on that row or untick <strong>Show</strong>.
            </div>
            <div className="w3-small w3-text-grey" style={{ marginBottom: '8px' }}>
              Drag rows with the grip icon, or use the arrow buttons, to reorder fields and text blocks.
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="w3-table w3-bordered w3-small" style={{ minWidth: '960px', background: '#fff' }}>
                <thead>
                  <tr className="w3-theme-l5">
                    <th title="The field label shown on the public membership form.">Field</th>
                    <th title="The type of data this field stores, such as text, email, number, or dropdown." style={{ width: '140px' }}>Data Type</th>
                    <th title="For dropdowns, add options; for text blocks, add the body content shown to applicants.">Options / Content</th>
                    <th title="Turn the field on or off for the public membership form." style={{ width: '88px' }}>Show</th>
                    <th title="Require this field on a new membership application." style={{ width: '104px' }}>Required</th>
                    <th title="Require this field again on a renewal when the member already has an existing record." style={{ width: '134px' }}>Renewal req.</th>
                    <th title="Store the data in GreenSheet or only send it by email to the club." style={{ width: '150px' }}>Storage</th>
                    <th title="Only visible to the member and club admins in player profiles." style={{ width: '96px' }}>Personal</th>
                    <th title="Reorder the field in the form." style={{ width: '88px' }}>Move</th>
                    <th title="Hide or remove the field." style={{ width: '72px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {membershipCfg.fields.map((field, fieldIndex) => {
                    const isLockedProfileField = MEMBERSHIP_PROFILE_LOCKED_FIELD_KEYS.has(field.key);
                    return (
                    <tr
                      key={field.key}
                      draggable={!isLockedProfileField}
                      onDragStart={() => {
                        if (isLockedProfileField) return;
                        setDragMembershipFieldKey(field.key);
                      }}
                      onDragEnd={() => setDragMembershipFieldKey('')}
                      onDragOver={(e) => {
                        if (isLockedProfileField) return;
                        if (!dragMembershipFieldKey || dragMembershipFieldKey === field.key) return;
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        if (isLockedProfileField) return;
                        if (!dragMembershipFieldKey || dragMembershipFieldKey === field.key) return;
                        e.preventDefault();
                        reorderMembershipField(dragMembershipFieldKey, field.key);
                        setDragMembershipFieldKey('');
                      }}
                      style={dragMembershipFieldKey === field.key ? { opacity: 0.45 } : undefined}
                    >
                      <td>
                        <i className="fa fa-bars w3-text-grey" title="Drag to reorder" style={{ marginRight: '8px', cursor: 'move' }}></i>
                        {field.type === 'content' ? (
                          <input
                            className="w3-input w3-border w3-round"
                            maxLength={MEMBERSHIP_LABEL_MAX_LENGTH}
                            value={field.label || ''}
                            onChange={e => updateMembershipFieldConfig(field.key, { label: e.target.value })}
                            placeholder="Heading (optional)"
                            disabled={isLockedProfileField}
                          />
                        ) : (
                          <input
                            className="w3-input w3-border w3-round"
                            maxLength={MEMBERSHIP_LABEL_MAX_LENGTH}
                            value={field.label || ''}
                            onChange={e => updateMembershipFieldConfig(field.key, { label: e.target.value })}
                            placeholder={field.isCustom ? '' : 'Field label for this club'}
                            disabled={isLockedProfileField}
                          />
                        )}
                      </td>
                      <td>
                        {field.isCustom ? (
                          <select
                            className="w3-select w3-border w3-round"
                            value={sanitizeMembershipCustomType(field.type)}
                            onChange={e => updateMembershipFieldConfig(field.key, {
                              type: sanitizeMembershipCustomType(e.target.value),
                              options: e.target.value === 'select' ? (Array.isArray(field.options) ? field.options : []) : [],
                              detail: e.target.value === 'content' ? (field.detail || '') : '',
                              required: e.target.value === 'content' ? false : !!field.required,
                              requiredOnRenewal: e.target.value === 'content' ? false : !!field.requiredOnRenewal,
                              storage: e.target.value === 'content' ? 'email-only' : (field.storage || 'email-only'),
                              personal: e.target.value === 'content' ? false : (field.personal !== false),
                            })}>
                            {MEMBERSHIP_CUSTOM_FIELD_TYPES.map(type => (
                              <option key={type} value={type}>{type === 'content' ? 'Text block (display only)' : type}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="w3-text-grey">{field.type}</span>
                        )}
                      </td>
                      <td>
                        {field.type === 'select' && field.isCustom ? (
                          <input
                            className="w3-input w3-border w3-round"
                            placeholder="Comma-separated options"
                            value={Array.isArray(field.options) ? field.options.join(', ') : ''}
                            onChange={e => updateMembershipFieldConfig(field.key, {
                              options: sanitizeMembershipCustomOptions(e.target.value.split(',')),
                            })}
                          />
                        ) : field.type === 'content' && field.isCustom ? (
                          <textarea
                            className="w3-input w3-border w3-round"
                            rows={3}
                            maxLength={MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH}
                            placeholder="Body text shown in the form. Use line breaks to match your printed layout."
                            value={field.detail || ''}
                            onChange={e => updateMembershipFieldConfig(field.key, {
                              detail: e.target.value,
                            })}
                          />
                        ) : field.type === 'select' ? (
                          (() => {
                            const { options, note } = resolveBuiltInSelectOptions(field);
                            return (
                              <div>
                                <div className="w3-small">{options.length ? options.join(', ') : '-'}</div>
                                <div className="w3-tiny w3-text-grey">{note}</div>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="w3-text-light-grey">-</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!field.enabled}
                          disabled={isLockedProfileField}
                          onChange={e => updateMembershipFieldConfig(field.key, { enabled: !!e.target.checked })}
                        />
                      </td>
                      <td>
                        {field.type === 'content'
                          ? <span className="w3-text-light-grey">-</span>
                          : (
                            <input
                              type="checkbox"
                              checked={!!field.required}
                              disabled={isLockedProfileField || !field.enabled}
                              onChange={e => updateMembershipFieldConfig(field.key, { required: !!e.target.checked })}
                            />
                          )}
                      </td>
                      <td>
                        {field.type === 'content'
                          ? <span className="w3-text-light-grey">-</span>
                          : (
                            <input
                              type="checkbox"
                              checked={!!field.requiredOnRenewal}
                              disabled={isLockedProfileField || !field.enabled}
                              onChange={e => updateMembershipFieldConfig(field.key, { requiredOnRenewal: !!e.target.checked })}
                            />
                          )}
                      </td>
                      <td>
                        {field.type === 'content'
                          ? <span className="w3-text-light-grey">Display only</span>
                          : (
                            <select
                              className="w3-select w3-border w3-round"
                              value={field.storage || 'email-only'}
                              disabled={isLockedProfileField}
                              onChange={e => updateMembershipFieldConfig(field.key, { storage: e.target.value })}>
                              <option value="platform">Store in platform</option>
                              <option value="email-only">Email only</option>
                            </select>
                          )}
                      </td>
                      <td>
                        {field.type === 'content'
                          ? <span className="w3-text-light-grey">-</span>
                          : (
                            <input
                              type="checkbox"
                              checked={field.personal !== false}
                              disabled={isLockedProfileField}
                              onChange={e => updateMembershipFieldConfig(field.key, { personal: !!e.target.checked })}
                            />
                          )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            type="button"
                            className="w3-button w3-tiny w3-round w3-theme-l4"
                            title="Move up" aria-label="Move up"
                            disabled={isLockedProfileField || fieldIndex === 0}
                            onClick={() => moveMembershipFieldByOffset(field.key, -1)}>
                            <i className="fa fa-arrow-up"></i>
                          </button>
                          <button
                            type="button"
                            className="w3-button w3-tiny w3-round w3-theme-l4"
                            title="Move down" aria-label="Move down"
                            disabled={isLockedProfileField || fieldIndex === membershipCfg.fields.length - 1}
                            onClick={() => moveMembershipFieldByOffset(field.key, 1)}>
                            <i className="fa fa-arrow-down"></i>
                          </button>
                        </div>
                      </td>
                      <td>
                        {field.isCustom ? (
                          <button
                            type="button"
                            className="w3-button w3-tiny w3-round w3-pale-red"
                            onClick={() => removeMembershipField(field.key)}
                            title="Remove field" aria-label="Remove field">
                            <i className="fa fa-trash"></i>
                          </button>
                        ) : (!isLockedProfileField ? (
                          <button
                            type="button"
                            className="w3-button w3-tiny w3-round w3-theme-l4"
                            onClick={() => updateMembershipFieldConfig(field.key, { enabled: !field.enabled })}
                            title={field.enabled ? 'Hide field from member form' : 'Show field on member form'}
                            aria-label={field.enabled ? 'Hide field from member form' : 'Show field on member form'}>
                            <i className={`fa ${field.enabled ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        ) : null)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="w3-border-top" style={{ marginTop: '10px', paddingTop: '10px' }}>
              <div className="w3-small" style={{ fontWeight: 700, marginBottom: '8px' }}>
                <i className="fa fa-plus-circle w3-margin-right"></i>Add Custom Field / Text Block
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 2fr) minmax(130px, 1fr) minmax(170px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                <div>
                  <label className="w3-tiny w3-text-grey">
                    {newMembershipFieldType === 'content' ? 'Heading (optional for content block)' : 'Field Label'}
                  </label>
                  <input
                    className="w3-input w3-border w3-round w3-small"
                    maxLength={MEMBERSHIP_LABEL_MAX_LENGTH}
                    value={newMembershipFieldLabel}
                    onChange={e => setNewMembershipFieldLabel(e.target.value)}
                    placeholder={newMembershipFieldType === 'content' ? 'e.g. Section 4 - Registration' : 'e.g. Has coaching certificate'}
                  />
                </div>
                <div>
                  <label className="w3-tiny w3-text-grey">Data Type</label>
                  <select
                    className="w3-select w3-border w3-round w3-small"
                    value={newMembershipFieldType}
                    onChange={e => setNewMembershipFieldType(sanitizeMembershipCustomType(e.target.value))}>
                    {MEMBERSHIP_CUSTOM_FIELD_TYPES.map(type => (
                      <option key={type} value={type}>{type === 'content' ? 'Text block (display only)' : type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="w3-tiny w3-text-grey">Storage</label>
                  <select
                    className="w3-select w3-border w3-round w3-small"
                    value={newMembershipFieldStorage}
                    disabled={newMembershipFieldType === 'content'}
                    onChange={e => setNewMembershipFieldStorage(e.target.value === 'platform' ? 'platform' : 'email-only')}>
                    <option value="platform">Store in platform</option>
                    <option value="email-only">Email only</option>
                  </select>
                </div>
                <div>
                  <label className="w3-tiny w3-text-grey">Required</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '7px' }}>
                    <input
                      type="checkbox"
                      checked={!!newMembershipFieldRequired}
                      disabled={newMembershipFieldType === 'content'}
                      onChange={e => setNewMembershipFieldRequired(!!e.target.checked)}
                    />
                    <span className="w3-small">Yes</span>
                  </label>
                </div>
                <div>
                  <label className="w3-tiny w3-text-grey">Personal</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '7px' }}>
                    <input
                      type="checkbox"
                      checked={!!newMembershipFieldPersonal}
                      disabled={newMembershipFieldType === 'content'}
                      onChange={e => setNewMembershipFieldPersonal(!!e.target.checked)}
                    />
                    <span className="w3-small">Yes</span>
                  </label>
                </div>
                <button
                  type="button"
                  className="w3-button w3-small w3-theme w3-round"
                  onClick={addCustomMembershipField}>
                  <i className="fa fa-plus w3-margin-right"></i>Add
                </button>
              </div>
              {newMembershipFieldType === 'select' && (
                <div style={{ marginTop: '8px' }}>
                  <label className="w3-tiny w3-text-grey">Dropdown Options (comma separated)</label>
                  <input
                    className="w3-input w3-border w3-round w3-small"
                    value={newMembershipFieldOptions}
                    onChange={e => setNewMembershipFieldOptions(e.target.value)}
                    placeholder="Option A, Option B, Option C"
                  />
                </div>
              )}
              {newMembershipFieldType === 'content' && (
                <div style={{ marginTop: '8px' }}>
                  <label className="w3-tiny w3-text-grey">Content Text</label>
                  <textarea
                    className="w3-input w3-border w3-round w3-small"
                    rows={4}
                    maxLength={MEMBERSHIP_CONFIG_TEXT_MAX_LENGTH}
                    value={newMembershipFieldDetail}
                    onChange={e => setNewMembershipFieldDetail(e.target.value)}
                    placeholder="I, ________________________, agree to be bound by the club constitution and code of conduct."
                  />
                  <div className="w3-small w3-text-grey">
                    Text blocks are shown as non-editable content on the member form, like intro/header text.
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default MembershipFormBuilder;
